import { api, type ApiResponse } from '@/lib/api';
import type {
  ChannelActivityListResponse,
  ChannelCommentResponse,
  ChannelDashboardOverview,
  ChannelMediaListResponse,
  ChannelMediaPublishResult,
  ChannelMediaResponse,
  ChannelMetricsResponse,
  CreateChannelMediaRequest,
  ListResponse,
  UpdateChannelMediaRequest,
} from '@troia-v3/types';

export type MetricsRange = '7d' | '30d' | '90d';

export type CreateMediaBody = Omit<CreateChannelMediaRequest, 'channelId' | 'providerId' | 'origin'>;

export interface ListMediaParams {
  status?: string;
  mediaType?: string;
  page?: number;
  limit?: number;
}

export const channelDashboardApi = {
  async getOverview(channelId: string): Promise<ChannelDashboardOverview> {
    const res = await api.get<ApiResponse<ChannelDashboardOverview>>(`channel-dashboard/${channelId}/overview`);
    return res.data.data!;
  },
  async getMetrics(channelId: string, range: MetricsRange): Promise<ChannelMetricsResponse[]> {
    const res = await api.get<ApiResponse<ChannelMetricsResponse[]>>(`channel-dashboard/${channelId}/metrics`, {
      params: { range },
    });
    return res.data.data ?? [];
  },
  async sync(channelId: string, resources?: string[]): Promise<void> {
    await api.post(`channel-dashboard/${channelId}/sync`, resources ? { resources } : {});
  },

  // Publicações (F3b)
  async listMedia(channelId: string, params?: ListMediaParams): Promise<ChannelMediaListResponse> {
    const res = await api.get<ApiResponse<ChannelMediaListResponse>>(`channel-dashboard/${channelId}/media`, { params });
    return res.data.data!;
  },
  async createMedia(channelId: string, body: CreateMediaBody): Promise<ChannelMediaResponse> {
    const res = await api.post<ApiResponse<ChannelMediaResponse>>(`channel-dashboard/${channelId}/media`, body);
    return res.data.data!;
  },
  async updateMedia(channelId: string, mediaId: string, body: UpdateChannelMediaRequest): Promise<ChannelMediaResponse> {
    const res = await api.put<ApiResponse<ChannelMediaResponse>>(`channel-dashboard/${channelId}/media/${mediaId}`, body);
    return res.data.data!;
  },
  async publishMedia(channelId: string, mediaId: string): Promise<ChannelMediaPublishResult> {
    const res = await api.post<ApiResponse<ChannelMediaPublishResult>>(`channel-dashboard/${channelId}/media/${mediaId}/publish`, {});
    return res.data.data!;
  },
  async deleteMedia(channelId: string, mediaId: string): Promise<void> {
    await api.delete(`channel-dashboard/${channelId}/media/${mediaId}`);
  },

  // Comentários (F4b)
  async listComments(channelId: string, params?: { mediaId?: string; status?: string; page?: number; limit?: number }): Promise<ListResponse<ChannelCommentResponse>> {
    const res = await api.get<ApiResponse<ListResponse<ChannelCommentResponse>>>(`channel-dashboard/${channelId}/comments`, { params });
    return res.data.data!;
  },
  async replyComment(channelId: string, commentId: string, message: string): Promise<ChannelCommentResponse> {
    const res = await api.post<ApiResponse<ChannelCommentResponse>>(`channel-dashboard/${channelId}/comments/${commentId}/reply`, { message });
    return res.data.data!;
  },
  async hideComment(channelId: string, commentId: string, hidden: boolean): Promise<ChannelCommentResponse> {
    const res = await api.post<ApiResponse<ChannelCommentResponse>>(`channel-dashboard/${channelId}/comments/${commentId}/hide`, { hidden });
    return res.data.data!;
  },
  async deleteComment(channelId: string, commentId: string): Promise<ChannelCommentResponse> {
    const res = await api.delete<ApiResponse<ChannelCommentResponse>>(`channel-dashboard/${channelId}/comments/${commentId}`);
    return res.data.data!;
  },

  // Atividade (F5b)
  async listActivity(channelId: string, params?: { cursor?: string; limit?: number }): Promise<ChannelActivityListResponse> {
    const res = await api.get<ApiResponse<ChannelActivityListResponse>>(`channel-dashboard/${channelId}/activities`, { params });
    return res.data.data!;
  },
};
